import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Clock3 } from "lucide-react";
import { api } from "../../lib/api";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";

type Notice = {
  id: string; kind: string; title: string; body: string; due_at?: string;
  href: string; is_read: boolean;
};

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notice[]>([]);
  const [unread, setUnread] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/notifications", { params: { status: showAll ? "all" : "unread" } });
    setItems(data.items); setUnread(data.unread_count);
    window.dispatchEvent(new CustomEvent("remedikeep:notification-count", { detail: data.unread_count }));
  }, [showAll]);

  useEffect(() => { void load(); }, [load]);

  const markRead = async (ids: string[]) => {
    if (!ids.length) return;
    setBusy(true);
    try { await api.post("/notifications/read", { notification_ids: ids }); await load(); }
    finally { setBusy(false); }
  };

  const open = async (item: Notice) => {
    if (!item.is_read) await markRead([item.id]);
    navigate(item.href);
  };

  return (
    <>
      <PageHeader title="Notifications & Reminders" subtitle="Time-sensitive work, verification, and commission alerts"
        actions={<Button variant="outline" disabled={busy || unread === 0} onClick={() => markRead(items.filter(x => !x.is_read).map(x => x.id))}>
          <CheckCheck className="w-4 h-4 mr-2" /> Mark all read
        </Button>} />
      <div className="p-6 max-w-5xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-600">{unread} unread reminder{unread === 1 ? "" : "s"}</div>
          <button className="text-sm underline" onClick={() => setShowAll(v => !v)}>
            {showAll ? "Show unread only" : "Show all"}
          </button>
        </div>
        {items.length === 0 ? (
          <div className="border rounded-xl bg-white p-12 text-center text-slate-500">
            <Bell className="w-8 h-8 mx-auto mb-3" /> You are all caught up.
          </div>
        ) : items.map(item => (
          <button key={item.id} onClick={() => open(item)}
            className={`w-full text-left border rounded-xl p-4 flex gap-3 hover:border-slate-400 ${item.is_read ? "bg-slate-50" : "bg-white border-slate-300"}`}>
            <Clock3 className="w-5 h-5 mt-0.5 text-slate-500" />
            <span className="flex-1">
              <span className="font-medium block">{item.title}</span>
              <span className="text-sm text-slate-600 block mt-1">{item.body}</span>
              {item.due_at && <span className="text-xs text-slate-500 block mt-2">{new Date(item.due_at).toLocaleString()}</span>}
            </span>
            {!item.is_read && <span className="w-2.5 h-2.5 rounded-full bg-blue-600 mt-1" aria-label="Unread" />}
          </button>
        ))}
      </div>
    </>
  );
}
